/**
 * Utility functions for managing cookies.
 * Used for storing last business login information for convenience.
 */

/**
 * Set a cookie with the given name, value, and expiration days.
 */
export const setCookie = (
  name: string,
  value: string,
  days: number = 30
): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  // Encode value to handle special characters
  const encodedValue = encodeURIComponent(value);
  document.cookie = `${name}=${encodedValue};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  console.log(`Cookie set: ${name}=${encodedValue}`);
};

/**
 * Get a cookie value by name.
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      const value = c.substring(nameEQ.length, c.length);
      return decodeURIComponent(value);
    }
  }
  return null;
};

/**
 * Delete a cookie by name.
 */
export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

/**
 * Cookie keys for last business login.
 */
export const LAST_BUSINESS_COOKIE = "last_business_login";

/** Maximum number of recent businesses to keep in the cookie. */
const MAX_RECENT_BUSINESSES = 20;

export type LastBusinessEntry = { slug: string; name: string };

/**
 * Get the list of recent business logins (most recent first).
 */
export const getLastBusinessLogins = (): LastBusinessEntry[] => {
  const cookieValue = getCookie(LAST_BUSINESS_COOKIE);
  if (!cookieValue) return [];

  try {
    const parsed = JSON.parse(cookieValue);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    // Legacy: single object stored as before
    if (
      parsed &&
      typeof parsed.slug === "string" &&
      typeof parsed.name === "string"
    ) {
      return [parsed];
    }
    return [];
  } catch {
    return [];
  }
};

/**
 * Get the single most recent business login (for backward compatibility).
 */
export const getLastBusinessLogin = (): LastBusinessEntry | null => {
  const list = getLastBusinessLogins();
  return list.length > 0 ? list[0] : null;
};

/**
 * Add a business to recent logins. Prepends to the list, dedupes by slug,
 * and caps at MAX_RECENT_BUSINESSES. Does not replace the list.
 */
export const setLastBusinessLogin = (
  businessSlug: string,
  businessName: string
): void => {
  const list = getLastBusinessLogins();
  const entry: LastBusinessEntry = { slug: businessSlug, name: businessName };
  const withoutThisSlug = list.filter((b) => b.slug !== businessSlug);
  const newList = [entry, ...withoutThisSlug].slice(0, MAX_RECENT_BUSINESSES);
  const data = JSON.stringify(newList);
  setCookie(LAST_BUSINESS_COOKIE, data, 30);
};

/**
 * Clear last business login information.
 */
export const clearLastBusinessLogin = (): void => {
  deleteCookie(LAST_BUSINESS_COOKIE);
};
